import { Input, Select } from '@/components/ui/FormControls';
import type { CatalogField, CatalogValues, CatalogValue } from '@/hooks/useCatalog';
import DatePicker from '@/components/common/DatePicker';
import FieldHint from '@/components/common/FieldHint';
import { catalogFieldGuidance, fieldHelpAttributes } from '@/lib/fieldGuidance';
export const catalogInput =
  'min-h-11 min-w-0 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60';
export default function CatalogFields({
  fields,
  values,
  onChange,
  errors = {},
  prefix = 'catalog',
}: {
  fields: CatalogField[];
  values: CatalogValues;
  onChange: (v: CatalogValues) => void;
  errors?: Record<string, string>;
  prefix?: string;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields
        .filter((f) => !f.when || values[f.when.field] === f.when.equals)
        .map((f) => {
          const id = `${prefix}-${f.code}`,
            v = values[f.code];
          const set = (value: CatalogValue) => onChange({ ...values, [f.code]: value });
          const options = f.options || [];
          const help = catalogFieldGuidance(f, fields);
          const describedBy = [`${id}-help`, errors[f.code] ? `${id}-error` : '']
            .filter(Boolean)
            .join(' ');
          const retained =
            typeof v === 'string' &&
            !!v &&
            !options.some((o) => o.value === v && o.active !== false);
          return (
            <div key={f.code} className="space-y-1">
              <label htmlFor={id} className="text-sm">
                {f.label}
                {f.required ? ' *' : ''}
              </label>
              {f.type === 'date' ? (
                <DatePicker
                  id={id}
                  helpLabel={f.label}
                  help={help}
                  aria-invalid={!!errors[f.code]}
                  aria-describedby={describedBy}
                  value={typeof v === 'string' ? v : undefined}
                  onChange={(d) => set(d || '')}
                />
              ) : f.type === 'boolean' ? (
                <Select
                  id={id}
                  helpLabel={f.label}
                  help={help}
                  aria-invalid={!!errors[f.code]}
                  aria-describedby={describedBy}
                  className={catalogInput}
                  value={v === true ? 'true' : v === false ? 'false' : ''}
                  onChange={(e) => set(e.target.value === '' ? null : e.target.value === 'true')}
                >
                  <option value="">Choose</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              ) : f.type === 'select' ? (
                <Select
                  id={id}
                  helpLabel={f.label}
                  help={help}
                  aria-invalid={!!errors[f.code]}
                  aria-describedby={describedBy}
                  className={catalogInput}
                  value={typeof v === 'string' ? v : ''}
                  onChange={(e) => set(e.target.value)}
                >
                  <option value="">Select {f.label.toLowerCase()}</option>
                  {retained && <option value={String(v)}>{String(v)} (saved value)</option>}
                  {options
                    .filter((o) => o.active !== false)
                    .map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                </Select>
              ) : f.type === 'multiselect' ? (
                <fieldset
                  id={id}
                  {...fieldHelpAttributes({ label: f.label, type: 'multiselect' }, help)}
                  aria-invalid={!!errors[f.code]}
                  aria-describedby={describedBy}
                  tabIndex={0}
                  className="max-h-52 space-y-1 overflow-auto rounded-xl border border-input p-2"
                >
                  <legend className="sr-only">{f.label}</legend>
                  {options
                    .filter((o) => o.active !== false || (Array.isArray(v) && v.includes(o.value)))
                    .map((o) => (
                      <label key={o.value} className="flex min-h-11 items-center gap-2 text-sm">
                        <Input
                          type="checkbox"
                          helpLabel={`${f.label}: ${o.label}`}
                          help={`Select this ${f.label.toLowerCase()} option. ${help}`}
                          className="h-5 w-5 accent-primary"
                          checked={Array.isArray(v) && v.includes(o.value)}
                          onChange={(e) =>
                            set(
                              e.target.checked
                                ? [...(Array.isArray(v) ? v : []), o.value]
                                : (Array.isArray(v) ? v : []).filter((k) => k !== o.value),
                            )
                          }
                        />
                        {o.label}
                        {o.active === false ? ' (saved option)' : ''}
                      </label>
                    ))}
                </fieldset>
              ) : (
                <Input
                  id={id}
                  helpLabel={f.label}
                  help={help}
                  aria-invalid={!!errors[f.code]}
                  aria-describedby={describedBy}
                  className={catalogInput}
                  type={f.type === 'number' ? 'number' : 'text'}
                  inputMode={f.type === 'number' ? 'decimal' : undefined}
                  min={f.min}
                  max={f.max}
                  maxLength={f.maxLength || 1000}
                  value={typeof v === 'string' || typeof v === 'number' ? v : ''}
                  onChange={(e) =>
                    set(
                      f.type === 'number'
                        ? e.target.value === ''
                          ? null
                          : Number(e.target.value)
                        : e.target.value,
                    )
                  }
                />
              )}
              <FieldHint id={`${id}-help`}>{help.split('. ')[0]}.</FieldHint>
              {errors[f.code] && (
                <p id={`${id}-error`} role="alert" className="text-sm text-destructive">
                  {errors[f.code]}
                </p>
              )}
            </div>
          );
        })}
    </div>
  );
}
